/**
 * Typed query/mutation hooks on top of openapi-fetch (§6.1/6.2). Feature code uses these rather
 * than calling `api` directly so Phase D can swap mocks for the real API in one place.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api, fetchJson, req } from './client';
import { mergePlaceholders, placeholderFromRecord } from '@/features/upload/reconcile';
import { useUploadStore } from '@/stores/upload';
import { keys, type BoardAssetsParams } from './keys';
import { fetchAllPages } from './paginate';
import type { Asset, AssetCard, AssetCardPage, Board, BoardAssetSort, BoardTreeNode, Comment, CommentPage, Library, Me, MediaUrls, SearchRequest, SearchResponse, SpriteIndex, Transcript, Workspace } from './types';
import { GRID_PAGE_SIZE, MEDIA_URL_STALE_MS } from '@/config/constants';
import type { BoardCardEntry } from '@/features/grid/types';

// --- auth / me ----------------------------------------------------------------------------
export function useMe(enabled = true) {
  return useQuery({ queryKey: keys.me(), queryFn: () => req(api.GET('/auth/me')), enabled, staleTime: 5 * 60_000, retry: false });
}

export async function fetchMe(): Promise<Me> {
  return req(api.GET('/auth/me'));
}

// --- workspace -----------------------------------------------------------------------------
export function useWorkspace(workspaceId: string | undefined) {
  return useQuery({
    queryKey: keys.workspace(workspaceId ?? ''),
    queryFn: () => req(api.GET('/workspaces/{workspaceId}', { params: { path: { workspaceId: workspaceId! } } })) as Promise<Workspace>,
    enabled: !!workspaceId,
    staleTime: 5 * 60_000,
  });
}

export function useLibraries(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.libraries(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/libraries', { params: { path: { workspaceId: wsId! } } }))).items as Library[],
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });
}

/** Board tree root: `GET /boards/{id}/tree` (depth-limited), used by the sidebar for lazy expansion. */
export function useBoardSubtree(boardId: string | undefined, depth = 1, enabled = true) {
  return useQuery({
    queryKey: [...keys.boardTree(boardId ?? ''), depth],
    queryFn: () => req(api.GET('/boards/{boardId}/tree', { params: { path: { boardId: boardId! }, query: { depth } } })) as Promise<BoardTreeNode>,
    enabled: !!boardId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useLibraryBoards(libraryId: string | undefined) {
  return useQuery({
    queryKey: keys.libraryBoards(libraryId ?? ''),
    queryFn: async () => (await req(api.GET('/libraries/{libraryId}/boards', { params: { path: { libraryId: libraryId! } } }))).items as Board[],
    enabled: !!libraryId,
  });
}

export function useBoard(boardId: string | undefined, opts?: Partial<UseQueryOptions<Board>>) {
  return useQuery({
    queryKey: keys.board(boardId ?? ''),
    queryFn: () => req(api.GET('/boards/{boardId}', { params: { path: { boardId: boardId! } } })) as Promise<Board>,
    enabled: !!boardId,
    ...opts,
  });
}

export function useBoardChildren(boardId: string | undefined) {
  return useQuery({
    queryKey: keys.boardChildren(boardId ?? ''),
    queryFn: async () => (await req(api.GET('/boards/{boardId}/children', { params: { path: { boardId: boardId! } } }))).items as Board[],
    enabled: !!boardId,
  });
}

export type BoardAssetsPage = Omit<AssetCardPage, 'items'> & { items: BoardCardEntry[] };

/** §2.12 cursor-paginated infinite query. Placeholder cards (§3.6) are injected into page 0 by the upload manager. */
function livePlaceholders(boardId: string) {
  const st = useUploadStore.getState();
  const out = [];
  for (const id of st.order) {
    const r = st.records[id];
    if (r && r.target_board_id === boardId && !r.is_new_version && r.status !== 'cancelled' && r.status !== 'ready') out.push(placeholderFromRecord(r));
  }
  return out;
}

export function useBoardAssets(boardId: string | undefined, params: BoardAssetsParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.boardAssets(boardId ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(
        api.GET('/boards/{boardId}/assets', {
          params: {
            path: { boardId: boardId! },
            query: {
              cursor: pageParam,
              limit: GRID_PAGE_SIZE,
              sort: params.sort,
              include_descendants: params.includeDescendants,
              ...(params.filter === 'review' ? { approval: ['needs_review' as const] } : {}),
            },
          },
        }),
      )) as BoardAssetsPage,
    select: (data) => {
      // §3.6 — re-inject live placeholders missing from fresh server data (first page only)
      const live = livePlaceholders(boardId ?? '');
      if (live.length === 0) return data;
      const pages = data.pages.map((p, i) => (i === 0 ? { ...p, items: mergePlaceholders(p.items, live, true) } : p));
      return { ...data, pages };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!boardId && enabled,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });
}

export function defaultSort(sort: string | null | undefined): BoardAssetSort {
  const ok: BoardAssetSort[] = ['added_at:desc', 'added_at:asc', 'name:asc', 'name:desc', 'size:desc', 'size:asc', 'created_at:desc', 'created_at:asc', 'sort_order:asc'];
  return ok.includes(sort as BoardAssetSort) ? (sort as BoardAssetSort) : 'added_at:desc';
}

// --- assets --------------------------------------------------------------------------------
export function useAsset(assetId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.asset(assetId ?? ''),
    queryFn: () => req(api.GET('/assets/{assetId}', { params: { path: { assetId: assetId! } } })) as Promise<Asset>,
    enabled: !!assetId && enabled,
    staleTime: MEDIA_URL_STALE_MS,
  });
}

export function useVersions(assetId: string | undefined) {
  return useQuery({
    queryKey: keys.versions(assetId ?? ''),
    queryFn: async () => (await req(api.GET('/assets/{assetId}/versions', { params: { path: { assetId: assetId! } } }))).items,
    enabled: !!assetId,
  });
}

export function useVersionMedia(versionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['media', versionId ?? ''],
    queryFn: () => req(api.GET('/versions/{versionId}/media', { params: { path: { versionId: versionId! } } })) as Promise<MediaUrls>,
    enabled: !!versionId && enabled,
    staleTime: MEDIA_URL_STALE_MS,
  });
}

export function useComments(assetId: string | undefined, filter: 'all' | 'unresolved' | 'mine' = 'all', versionId?: string, shareToken?: string | null) {
  return useQuery({
    queryKey: keys.comments(assetId ?? ''),
    queryFn: () =>
      (shareToken
        ? req(api.GET('/public/share/{token}/assets/{assetId}/comments', { params: { path: { token: shareToken, assetId: assetId! }, query: { limit: 200 } } }))
        : req(api.GET('/assets/{assetId}/comments', { params: { path: { assetId: assetId! }, query: { filter: 'all', sort: 'media_time', limit: 200 } } }))) as Promise<CommentPage>,
    enabled: !!assetId,
    select: (page) => {
      let items = page.items;
      if (filter === 'unresolved') items = items.filter((c) => !c.resolved);
      if (versionId) items = items.filter((c) => c.version_id === versionId);
      return { ...page, items };
    },
  });
}

export function useSpriteIndex(versionId: string | undefined, url: string | null | undefined, inline?: SpriteIndex | null) {
  return useQuery({
    queryKey: keys.spriteIndex(versionId ?? ''),
    queryFn: () => fetchJson<SpriteIndex>(url!),
    enabled: !!versionId && !!url && !inline,
    staleTime: Infinity,
    initialData: inline ?? undefined,
  });
}

export function useTranscript(versionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.transcript(versionId ?? ''),
    queryFn: () => req(api.GET('/versions/{versionId}/transcript', { params: { path: { versionId: versionId! } } })) as Promise<Transcript>,
    enabled: !!versionId && enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useRegions(versionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.regions(versionId ?? ''),
    queryFn: async () => (await req(api.GET('/versions/{versionId}/regions', { params: { path: { versionId: versionId! } } }))).items,
    enabled: !!versionId && enabled,
    staleTime: Infinity,
  });
}

export function useProcessing(versionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.processing(versionId ?? ''),
    queryFn: () => req(api.GET('/versions/{versionId}/processing', { params: { path: { versionId: versionId! } } })),
    enabled: !!versionId && enabled,
    refetchInterval: enabled ? 5000 : false,
  });
}

export function useActivity(assetId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.activity(assetId ?? ''),
    queryFn: async () => (await req(api.GET('/assets/{assetId}/activity', { params: { path: { assetId: assetId! } } }))).items,
    enabled: !!assetId && enabled,
  });
}

// --- workspace catalogues -------------------------------------------------------------------
export function useTags(wsId: string | undefined) {
  // Drained page by page at the API cap (200); a workspace may hold up to 5,000 tags and `limit=500` answered 422.
  return useQuery({ queryKey: keys.tags(wsId ?? ''), queryFn: () => fetchAllPages((cursor, limit) => req(api.GET('/workspaces/{workspaceId}/tags', { params: { path: { workspaceId: wsId! }, query: { cursor, limit } } }))), enabled: !!wsId, staleTime: 5 * 60_000 });
}
export function useFields(wsId: string | undefined) {
  return useQuery({ queryKey: keys.fields(wsId ?? ''), queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/custom-fields', { params: { path: { workspaceId: wsId! } } }))).items, enabled: !!wsId, staleTime: 5 * 60_000 });
}
export function useMembers(wsId: string | undefined) {
  return useQuery({ queryKey: keys.members(wsId ?? ''), queryFn: () => fetchAllPages((cursor, limit) => req(api.GET('/workspaces/{workspaceId}/members', { params: { path: { workspaceId: wsId! }, query: { cursor, limit } } }))), enabled: !!wsId });
}
export function useRoles(wsId: string | undefined) {
  return useQuery({ queryKey: keys.roles(wsId ?? ''), queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/roles', { params: { path: { workspaceId: wsId! } } }))).items, enabled: !!wsId });
}
export function usePeople(wsId: string | undefined) {
  return useQuery({ queryKey: keys.people(wsId ?? ''), queryFn: () => fetchAllPages((cursor, limit) => req(api.GET('/workspaces/{workspaceId}/people', { params: { path: { workspaceId: wsId! }, query: { cursor, limit } } }))), enabled: !!wsId });
}
export function useShareLinks(wsId: string | undefined) {
  return useQuery({ queryKey: keys.shareLinks(wsId ?? ''), queryFn: () => fetchAllPages((cursor, limit) => req(api.GET('/workspaces/{workspaceId}/share-links', { params: { path: { workspaceId: wsId! }, query: { cursor, limit } } }))), enabled: !!wsId });
}
export function useSavedSearches(wsId: string | undefined) {
  return useQuery({ queryKey: keys.savedSearches(wsId ?? ''), queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/saved-searches', { params: { path: { workspaceId: wsId! } } }))).items, enabled: !!wsId });
}

// --- search ----------------------------------------------------------------------------------
export function useSearch(request: SearchRequest | null) {
  return useInfiniteQuery({
    queryKey: keys.search(request ?? ({} as SearchRequest)),
    queryFn: async ({ pageParam }) => (await req(api.POST('/search', { body: { ...request!, cursor: pageParam ?? null } }))) as SearchResponse,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!request,
    staleTime: 30_000,
  });
}

// --- mutation helpers -------------------------------------------------------------------------
export function useInvalidate() {
  const qc = useQueryClient();
  return (key: readonly unknown[]) => qc.invalidateQueries({ queryKey: key });
}

/** Patch an AssetCard everywhere it appears (board pages, search pages, asset query). */
export function patchCardEverywhere(qc: ReturnType<typeof useQueryClient>, card: AssetCard): void {
  qc.setQueriesData<{ pages: BoardAssetsPage[] }>({ queryKey: ['board-assets'] }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const idx = p.items.findIndex((c) => c.id === card.id);
      if (idx < 0) return p;
      touched = true;
      const items = p.items.slice();
      items[idx] = { ...(items[idx] as AssetCard), ...card };
      return { ...p, items };
    });
    return touched ? { ...old, pages } : old;
  });
  qc.setQueriesData<{ pages: SearchResponse[] }>({ queryKey: ['search'] }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const idx = p.items.findIndex((r) => r.asset.id === card.id);
      if (idx < 0) return p;
      touched = true;
      const items = p.items.slice();
      items[idx] = { ...items[idx]!, asset: { ...items[idx]!.asset, ...card } };
      return { ...p, items };
    });
    return touched ? { ...old, pages } : old;
  });
  qc.setQueryData<Asset>(keys.asset(card.id), (old) => (old ? { ...old, ...card, active_version: old.active_version, boards: old.boards, tags: old.tags, custom_field_values: old.custom_field_values, media: old.media } : old));
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, patch }: { assetId: string; patch: { title?: string; description?: string | null } }) => req(api.PATCH('/assets/{assetId}', { params: { path: { assetId } }, body: patch })) as Promise<Asset>,
    onMutate: async ({ assetId, patch }) => {
      await qc.cancelQueries({ queryKey: keys.asset(assetId) });
      const prev = qc.getQueryData<Asset>(keys.asset(assetId));
      if (prev) qc.setQueryData<Asset>(keys.asset(assetId), { ...prev, ...patch, updated_at: new Date().toISOString() });
      return { prev };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.asset(v.assetId), ctx.prev);
    },
    onSuccess: (asset) => patchCardEverywhere(qc, asset),
  });
}

export function useSetApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, status, note }: { assetId: string; status: Asset['approval_status']; note?: string }) => req(api.PUT('/assets/{assetId}/approval', { params: { path: { assetId } }, body: { status, note } })) as Promise<Asset>,
    onMutate: async ({ assetId, status }) => {
      await qc.cancelQueries({ queryKey: keys.asset(assetId) });
      const prev = qc.getQueryData<Asset>(keys.asset(assetId));
      if (prev) qc.setQueryData<Asset>(keys.asset(assetId), { ...prev, approval_status: status });
      return { prev };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.asset(v.assetId), ctx.prev);
    },
    onSuccess: (asset) => patchCardEverywhere(qc, asset),
  });
}

export function useBulkAction(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { asset_ids: string[]; action: 'move_to_board' | 'copy_to_board' | 'add_tags' | 'remove_tags' | 'set_approval' | 'delete' | 'restore' | 'download_zip' | 'set_custom_field'; params?: Record<string, unknown> }) =>
      req(api.POST('/workspaces/{workspaceId}/assets/bulk', { params: { path: { workspaceId: wsId } }, body: body as never })),
    onSuccess: (_r, v) => {
      if (v.action === 'delete' || v.action === 'move_to_board') {
        // optimistic removal happens through WS events; also drop locally for snappiness
        const ids = new Set(v.asset_ids);
        const from = v.params?.['from_board_id'] as string | undefined;
        qc.setQueriesData<{ pages: BoardAssetsPage[] }>({ queryKey: from ? ['board-assets', from] : ['board-assets'] }, (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((c) => !ids.has(c.id)) })) } : old));
      }
      // M7: WS events patch the cache while the socket is up; these refetches are the fallback
      const target = v.params?.['board_id'] as string | undefined;
      if (v.action === 'copy_to_board' && target) {
        void qc.invalidateQueries({ queryKey: ['board-assets', target] });
        void qc.invalidateQueries({ queryKey: keys.board(target) });
      }
      if (v.action === 'add_tags' || v.action === 'remove_tags' || v.action === 'set_approval' || v.action === 'set_custom_field' || v.action === 'restore') {
        if (v.asset_ids.length <= 50) for (const id of v.asset_ids) void qc.invalidateQueries({ queryKey: keys.asset(id) });
        else void qc.invalidateQueries({ queryKey: ['asset'] });
        void qc.invalidateQueries({ queryKey: ['board-assets'] });
        void qc.invalidateQueries({ queryKey: ['search'] });
      }
      void qc.invalidateQueries({ queryKey: ['board-tree'] });
    },
  });
}

export function useAddAssetsToBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ boardId, assetIds, fromBoardId, mode }: { boardId: string; assetIds: string[]; fromBoardId: string | null; mode: 'move' | 'copy' }) =>
      req(api.POST('/boards/{boardId}/assets', { params: { path: { boardId } }, body: { asset_ids: assetIds, ...(mode === 'copy' && fromBoardId ? { copy_from_board_id: fromBoardId } : {}) } as never })),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ['board-assets', v.boardId] });
      void qc.invalidateQueries({ queryKey: ['board-tree'] });
    },
  });
}

export type { Comment };

// --- release1/C ---
import type { AiTagReviewParams, DuplicatesParams } from './keys';
import type { Schemas as SchemasC } from './types';
export type DuplicateGroup = SchemasC['DuplicateGroup'];
export type DuplicateGroupPage = SchemasC['DuplicateGroupPage'];
export type DuplicateMember = SchemasC['DuplicateMember'];
export type DuplicateMergeResult = SchemasC['DuplicateMergeResult'];
export type AssetLookupResponse = SchemasC['AssetLookupResponse'];
export type AiTagReviewBucket = SchemasC['AiTagReviewBucket'];
export type AiTagReviewPage = SchemasC['AiTagReviewPage'];
export type AiTagReviewResult = SchemasC['AiTagReviewResult'];
export type TagMergeResult = SchemasC['TagMergeResult'];

/** F5 — duplicate groups, paged by representative asset id. */
export function useDuplicateGroups(wsId: string | undefined, params: DuplicatesParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.duplicates(wsId ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(
        api.GET('/workspaces/{workspaceId}/duplicates', {
          params: { path: { workspaceId: wsId! }, query: { mode: params.mode, threshold: params.threshold, include_dismissed: params.includeDismissed, board_id: params.boardId ?? undefined, cursor: pageParam, limit: 20 } },
        }),
      )) as DuplicateGroupPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!wsId && enabled,
    staleTime: 30_000,
  });
}

function invalidateAfterMerge(qc: ReturnType<typeof useQueryClient>, wsId: string, keptId: string, mergedIds: string[]) {
  void qc.invalidateQueries({ queryKey: keys.duplicatesAll(wsId) });
  void qc.invalidateQueries({ queryKey: keys.asset(keptId) });
  for (const id of mergedIds) qc.removeQueries({ queryKey: keys.asset(id) });
  void qc.invalidateQueries({ queryKey: ['board-assets'] });
  void qc.invalidateQueries({ queryKey: ['search'] });
}

export function useMergeDuplicates(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { keep_asset_id: string; merge_asset_ids: string[] }) =>
      req(api.POST('/workspaces/{workspaceId}/duplicates/merge', { params: { path: { workspaceId: wsId } }, body, headers: { 'Idempotency-Key': crypto.randomUUID() } })) as Promise<DuplicateMergeResult>,
    onSuccess: (r) => invalidateAfterMerge(qc, wsId, r.kept.id, r.merged_asset_ids),
  });
}

export function useDismissDuplicates(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asset_ids: string[]) => req(api.POST('/workspaces/{workspaceId}/duplicates/dismiss', { params: { path: { workspaceId: wsId } }, body: { asset_ids } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.duplicatesAll(wsId) }),
  });
}

/** Upload-time lookup by content hash (F5). `sha256` empty → disabled. */
export function useAssetLookup(wsId: string | undefined, sha256: string | undefined) {
  return useQuery({
    queryKey: keys.assetLookup(wsId ?? '', sha256 ?? ''),
    queryFn: () => lookupAssetsBySha256(wsId!, sha256!),
    enabled: !!wsId && !!sha256,
    staleTime: 60_000,
  });
}
export function lookupAssetsBySha256(wsId: string, sha256: string): Promise<AssetLookupResponse> {
  return req(api.GET('/workspaces/{workspaceId}/assets/lookup', { params: { path: { workspaceId: wsId }, query: { sha256 } } })) as Promise<AssetLookupResponse>;
}

/** G2 — merge tags; filters that referenced a merged tag are rewritten server-side, so refetch everything tag-shaped. */
export function useMergeTags(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { into_tag_id: string; tag_ids: string[] }) =>
      req(api.POST('/workspaces/{workspaceId}/tags/merge', { params: { path: { workspaceId: wsId } }, body, headers: { 'Idempotency-Key': crypto.randomUUID() } })) as Promise<TagMergeResult>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
      void qc.invalidateQueries({ queryKey: keys.savedSearches(wsId) });
      void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['search'] });
      void qc.invalidateQueries({ queryKey: keys.aiTagReviewAll(wsId) });
    },
  });
}

export function useAiTagReview(wsId: string | undefined, params: AiTagReviewParams = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.aiTagReview(wsId ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(api.GET('/workspaces/{workspaceId}/tags/ai-review', { params: { path: { workspaceId: wsId! }, query: { board_id: params.boardId ?? undefined, cursor: pageParam, limit: 20 } } }))) as AiTagReviewPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!wsId && enabled,
    staleTime: 30_000,
  });
}

export function useReviewAiTags(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tag_id: string; action: 'keep' | 'dismiss'; asset_ids?: string[]; board_id?: string | null }) =>
      req(api.POST('/workspaces/{workspaceId}/tags/ai-review', { params: { path: { workspaceId: wsId } }, body })) as Promise<AiTagReviewResult>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.aiTagReviewAll(wsId) });
      void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
      void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

// --- release1/A ---
import type { BoardAssetsParamsR1 } from './keys';
import { keepPreviousData } from '@tanstack/react-query';
import type { Schemas as SchemasA } from './types';
export type BoardView = SchemasA['BoardView'];
export type BoardViewCreate = SchemasA['BoardViewCreate'];
export type BoardViewUpdate = SchemasA['BoardViewUpdate'];
export type BoardAssetsPageR1 = SchemasA['BoardAssetsPage'];

/**
 * F1 filter spine: `useBoardAssets` plus `filters` (compact `BoardFilters` JSON) and `facets`. Without either the
 * request is byte-for-byte the legacy one, so paging/perf of unfiltered boards is unchanged. Same placeholder merge.
 */
export function useBoardAssetsR1(boardId: string | undefined, params: BoardAssetsParamsR1, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.boardAssetsR1(boardId ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(
        api.GET('/boards/{boardId}/assets', {
          params: {
            path: { boardId: boardId! },
            query: {
              cursor: pageParam,
              limit: GRID_PAGE_SIZE,
              sort: params.sort,
              include_descendants: params.includeDescendants,
              ...(params.filter === 'review' ? { approval: ['needs_review' as const] } : {}),
              ...(params.filters ? { filters: params.filters } : {}),
              ...(params.includeArchived ? { include_archived: true } : {}), // B's "Show archived" (S7a)
              // facets only on the first page (the API ignores them with a cursor anyway)
              ...(params.facets && !pageParam ? { facets: true } : {}),
            },
          },
        }),
      )) as BoardAssetsPageR1,
    select: (data) => {
      const live = livePlaceholders(boardId ?? '');
      if (live.length === 0) return data;
      const pages = data.pages.map((p, i) => (i === 0 ? { ...p, items: mergePlaceholders(p.items, live, true) } : p));
      return { ...data, pages };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!boardId && enabled,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });
}

/** F2 saved views: shared (`visibility=board`) plus the caller's private ones, API order (shared first). */
export function useBoardViews(boardId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.boardViews(boardId ?? ''),
    queryFn: async () => (await req(api.GET('/boards/{boardId}/views', { params: { path: { boardId: boardId! } } }))).items,
    enabled: !!boardId && enabled,
    staleTime: 60_000,
  });
}

export function useCreateBoardView(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BoardViewCreate) => req(api.POST('/boards/{boardId}/views', { params: { path: { boardId } }, body })),
    onSuccess: (view) => {
      qc.setQueryData<BoardView[]>(keys.boardViews(boardId), (old) => (old ? upsertView(old, view) : old));
      void qc.invalidateQueries({ queryKey: keys.boardViews(boardId) });
    },
  });
}

export function useUpdateBoardView(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, body }: { viewId: string; body: BoardViewUpdate }) => req(api.PATCH('/views/{viewId}', { params: { path: { viewId } }, body })),
    onSuccess: (view) => {
      qc.setQueryData<BoardView[]>(keys.boardViews(boardId), (old) => (old ? upsertView(old, view) : old));
      void qc.invalidateQueries({ queryKey: keys.boardViews(boardId) });
    },
  });
}

export function useDeleteBoardView(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => req(api.DELETE('/views/{viewId}', { params: { path: { viewId } } })),
    onSuccess: (_r, viewId) => {
      qc.setQueryData<BoardView[]>(keys.boardViews(boardId), (old) => old?.filter((v) => v.id !== viewId));
      void qc.invalidateQueries({ queryKey: keys.boardViews(boardId) });
    },
  });
}

/** Insert/replace a view keeping the list's order (shared first, then sort_order, then name); a new default demotes the old one. */
export function upsertView(list: readonly BoardView[], view: BoardView): BoardView[] {
  const rest = list.filter((v) => v.id !== view.id).map((v) => (view.is_default && v.is_default ? { ...v, is_default: false } : v));
  return [...rest, view].sort((a, b) => Number(b.visibility === 'board') - Number(a.visibility === 'board') || a.sort_order - b.sort_order || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id));
}

// --- release1/B ---
import type { AssetBoards, MemberPin, PinKind, TrashBulkResult, TrashItemRef, TrashPage } from './types';
import type { TrashParams } from './keys';
import { useTidyPrefs } from '@/stores/gridPrefs';

/** Boards hidden by archive (S7a): drop an asset from every cached board page (and the search pages). */
function dropCardEverywhere(qc: ReturnType<typeof useQueryClient>, ids: readonly string[]): void {
  const set = new Set(ids);
  qc.setQueriesData<{ pages: BoardAssetsPage[] }>({ queryKey: ['board-assets'] }, (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((c) => !set.has(c.id)) })) } : old));
  qc.setQueriesData<{ pages: SearchResponse[] }>({ queryKey: ['search'] }, (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((r) => !set.has(r.asset.id)) })) } : old));
}

// pins ------------------------------------------------------------------------------------------
export function usePins(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.pins(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/pins', { params: { path: { workspaceId: wsId! } } }))).items as MemberPin[],
    enabled: !!wsId,
    staleTime: 60_000,
  });
}

export function usePin(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, targetId }: { kind: PinKind; targetId: string }) => req(api.PUT('/workspaces/{workspaceId}/pins/{kind}/{targetId}', { params: { path: { workspaceId: wsId, kind, targetId } } })) as Promise<MemberPin>,
    onSuccess: (pin) => {
      qc.setQueryData<MemberPin[]>(keys.pins(wsId), (old) => (old && !old.some((p) => p.id === pin.id) ? [...old, pin].sort((a, b) => a.sort_order - b.sort_order) : old));
      void qc.invalidateQueries({ queryKey: keys.pins(wsId) });
    },
  });
}

export function useUnpin(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, targetId }: { kind: PinKind; targetId: string }) => req(api.DELETE('/workspaces/{workspaceId}/pins/{kind}/{targetId}', { params: { path: { workspaceId: wsId, kind, targetId } } })),
    onMutate: async ({ kind, targetId }) => {
      await qc.cancelQueries({ queryKey: keys.pins(wsId) });
      const prev = qc.getQueryData<MemberPin[]>(keys.pins(wsId));
      if (prev) qc.setQueryData<MemberPin[]>(keys.pins(wsId), prev.filter((p) => !(p.kind === kind && p.target_id === targetId)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.pins(wsId), ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.pins(wsId) }),
  });
}

export function useReorderPins(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pinIds: string[]) => req(api.POST('/workspaces/{workspaceId}/pins/reorder', { params: { path: { workspaceId: wsId } }, body: { pin_ids: pinIds } })) as Promise<{ items: MemberPin[] }>,
    onMutate: async (pinIds) => {
      await qc.cancelQueries({ queryKey: keys.pins(wsId) });
      const prev = qc.getQueryData<MemberPin[]>(keys.pins(wsId));
      if (prev) {
        const byId = new Map(prev.map((p) => [p.id, p]));
        qc.setQueryData<MemberPin[]>(keys.pins(wsId), pinIds.map((id, i) => ({ ...byId.get(id)!, sort_order: i })).filter((p) => p.id));
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.pins(wsId), ctx.prev);
    },
    onSuccess: (r) => qc.setQueryData<MemberPin[]>(keys.pins(wsId), r.items),
  });
}

// archive -----------------------------------------------------------------------------------------
export function useArchiveBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ boardId, archived }: { boardId: string; archived: boolean }) =>
      req(archived ? api.POST('/boards/{boardId}/archive', { params: { path: { boardId } } }) : api.POST('/boards/{boardId}/unarchive', { params: { path: { boardId } } })) as Promise<Board>,
    onSuccess: (board) => {
      qc.setQueryData<Board>(keys.board(board.id), (old) => (old ? { ...old, ...board, effective_mask: old.effective_mask, breadcrumbs: old.breadcrumbs } : old));
      void qc.invalidateQueries({ queryKey: ['library-boards'] });
      void qc.invalidateQueries({ queryKey: ['board-children'] });
      void qc.invalidateQueries({ queryKey: ['board-tree'] });
      void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['pins'] });
    },
  });
}

export function useArchiveAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, archived }: { assetId: string; archived: boolean }) =>
      req(archived ? api.POST('/assets/{assetId}/archive', { params: { path: { assetId } } }) : api.POST('/assets/{assetId}/unarchive', { params: { path: { assetId } } })) as Promise<Asset>,
    onSuccess: (asset, v) => {
      patchCardEverywhere(qc, asset);
      if (v.archived && !useTidyPrefs.getState().showArchived) dropCardEverywhere(qc, [asset.id]);
      else void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['pins'] });
    },
  });
}

/** `bulkAssetAction` with `archive` / `unarchive` (the legacy `useBulkAction` union predates them). */
export function useBulkArchive(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetIds, archived }: { assetIds: string[]; archived: boolean }) =>
      req(api.POST('/workspaces/{workspaceId}/assets/bulk', { params: { path: { workspaceId: wsId } }, body: { asset_ids: assetIds, action: archived ? 'archive' : 'unarchive' } as never })) as Promise<{ succeeded: string[]; failed: Array<{ asset_id: string; code: string; message?: string }> }>,
    onSuccess: (r, v) => {
      if (v.archived && !useTidyPrefs.getState().showArchived) dropCardEverywhere(qc, r.succeeded);
      else void qc.invalidateQueries({ queryKey: ['board-assets'] });
      if (r.succeeded.length <= 50) for (const id of r.succeeded) void qc.invalidateQueries({ queryKey: keys.asset(id) });
      else void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['pins'] });
    },
  });
}

// trash ---------------------------------------------------------------------------------------------
export const TRASH_PAGE_SIZE = 50;
export function useTrash(wsId: string | undefined, params: TrashParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.trash(wsId ?? '', params),
    queryFn: async ({ pageParam }) => (await req(api.GET('/workspaces/{workspaceId}/trash', { params: { path: { workspaceId: wsId! }, query: { kind: params.kind, cursor: pageParam, limit: TRASH_PAGE_SIZE } } }))) as TrashPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!wsId && enabled,
    staleTime: 15_000,
  });
}

function afterTrashChange(qc: ReturnType<typeof useQueryClient>, wsId: string, refs: readonly TrashItemRef[]): void {
  void qc.invalidateQueries({ queryKey: keys.trashAll(wsId) });
  for (const r of refs) {
    if (r.kind === 'asset') void qc.invalidateQueries({ queryKey: keys.asset(r.id) });
    else {
      void qc.invalidateQueries({ queryKey: keys.board(r.id) });
      void qc.invalidateQueries({ queryKey: keys.boardChildren(r.id) });
    }
  }
  void qc.invalidateQueries({ queryKey: ['library-boards'] });
  void qc.invalidateQueries({ queryKey: ['board-children'] });
  void qc.invalidateQueries({ queryKey: ['board-tree'] });
  void qc.invalidateQueries({ queryKey: ['board-assets'] });
  void qc.invalidateQueries({ queryKey: ['search'] });
  void qc.invalidateQueries({ queryKey: keys.pins(wsId) });
}

export function useRestoreTrash(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: TrashItemRef[]) => req(api.POST('/workspaces/{workspaceId}/trash/restore', { params: { path: { workspaceId: wsId } }, body: { items, all: false } })) as Promise<TrashBulkResult>,
    onSuccess: (r) => afterTrashChange(qc, wsId, r.succeeded),
  });
}

export function usePurgeTrash(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { items?: TrashItemRef[]; all?: boolean }) => req(api.POST('/workspaces/{workspaceId}/trash/purge', { params: { path: { workspaceId: wsId } }, body: { items: v.items, all: v.all ?? false } })) as Promise<TrashBulkResult>,
    onSuccess: (r) => {
      for (const ref of r.succeeded) qc.removeQueries({ queryKey: ref.kind === 'asset' ? keys.asset(ref.id) : keys.board(ref.id) });
      void qc.invalidateQueries({ queryKey: keys.trashAll(wsId) });
    },
  });
}

// multi-board ----------------------------------------------------------------------------------------
export function useAssetBoards(assetId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.assetBoards(assetId ?? ''),
    queryFn: () => req(api.GET('/assets/{assetId}/boards', { params: { path: { assetId: assetId! } } })) as Promise<AssetBoards>,
    enabled: !!assetId && enabled,
  });
}

/** `PUT /assets/{id}/boards`: set semantics; the first listed board becomes home (a reorder alone moves home). */
export function useSetAssetBoards() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, boardIds }: { assetId: string; boardIds: string[] }) => req(api.PUT('/assets/{assetId}/boards', { params: { path: { assetId } }, body: { board_ids: boardIds } })) as Promise<AssetBoards>,
    onSuccess: (r, v) => {
      qc.setQueryData<AssetBoards>(keys.assetBoards(v.assetId), r);
      const before = qc.getQueryData<Asset>(keys.asset(v.assetId));
      qc.setQueryData<Asset>(keys.asset(v.assetId), (old) => (old ? { ...old, boards: r.items, board_ids: r.items.map((b) => b.id), home_board_id: r.home_board_id ?? undefined } : old));
      const touched = new Set([...(before?.board_ids ?? []), ...r.items.map((b) => b.id)]);
      for (const b of touched) {
        void qc.invalidateQueries({ queryKey: ['board-assets', b] });
        void qc.invalidateQueries({ queryKey: keys.board(b) });
      }
      void qc.invalidateQueries({ queryKey: ['board-tree'] });
    },
  });
}

// listings with include_archived --------------------------------------------------------------------
export function useLibraryBoardsR1(libraryId: string | undefined, includeArchived: boolean) {
  return useQuery({
    queryKey: keys.libraryBoardsR1(libraryId ?? '', includeArchived),
    queryFn: async () => (await req(api.GET('/libraries/{libraryId}/boards', { params: { path: { libraryId: libraryId! }, query: { include_archived: includeArchived } } }))).items as Board[],
    enabled: !!libraryId,
  });
}

export function useBoardChildrenR1(boardId: string | undefined, includeArchived: boolean) {
  return useQuery({
    queryKey: keys.boardChildrenR1(boardId ?? '', includeArchived),
    queryFn: async () => (await req(api.GET('/boards/{boardId}/children', { params: { path: { boardId: boardId! }, query: { include_archived: includeArchived } } }))).items as Board[],
    enabled: !!boardId,
  });
}

/**
 * Facet counts for the board scope (`facets=true`, `limit=1`). Kept separate from the listing so unfiltered boards
 * still use the legacy query/paging (the facet path lists ready versions only).
 */
export function useBoardFacetsR1(boardId: string | undefined, params: { filters?: string; includeDescendants: boolean; includeArchived?: boolean }, enabled = true) {
  return useQuery({
    queryKey: ['board-facets', boardId ?? '', params] as const,
    queryFn: async () => (await req(api.GET('/boards/{boardId}/assets', { params: { path: { boardId: boardId! }, query: { limit: 1, facets: true, include_descendants: params.includeDescendants, ...(params.includeArchived ? { include_archived: true } : {}), ...(params.filters ? { filters: params.filters } : {}) } } }))).facets ?? null,
    enabled: !!boardId && enabled,
    placeholderData: keepPreviousData, // keep counts on screen while a filter change refetches
    staleTime: 15_000,
    gcTime: 5 * 60_000,
  });
}

// --- release2/A ---
import type { SystemBoardAssetsParams } from './keys';
export type SystemBoard = SchemasA['SystemBoard'];
export type SystemBoardKey = SchemasA['SystemBoardKey'];
export type SmartBoardCreate = SchemasA['SmartBoardCreate'];
export type SmartBoardDefinition = SchemasA['SmartBoardDefinition'];
export type SmartBoardSort = SchemasA['SmartBoardSort'];

/** S4 system boards in display order with exact counts (`duplicates` has `count: null` and a `link`). */
export function useSystemBoards(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.systemBoards(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/system-boards', { params: { path: { workspaceId: wsId! } } }))).items as SystemBoard[],
    enabled: !!wsId,
    staleTime: 60_000,
  });
}

/** S4 listing of one system board: same paging and facets as a smart board listing (no membership, no placeholders). */
export function useSystemBoardAssets(wsId: string | undefined, key: SystemBoardKey | undefined, params: SystemBoardAssetsParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.systemBoardAssets(wsId ?? '', key ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(
        api.GET('/workspaces/{workspaceId}/system-boards/{systemBoardKey}/assets', {
          params: {
            path: { workspaceId: wsId!, systemBoardKey: key! },
            query: {
              cursor: pageParam,
              limit: GRID_PAGE_SIZE,
              sort: params.sort,
              ...(params.filters ? { filters: params.filters } : {}),
              ...(params.includeArchived ? { include_archived: true } : {}),
              ...(params.facets && !pageParam ? { facets: true } : {}),
            },
          },
        }),
      )) as BoardAssetsPageR1,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!wsId && !!key && enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** S3 `POST /workspaces/{id}/smart-boards`; the new board shows up in the library / parent listings. */
export function useCreateSmartBoard(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SmartBoardCreate) => req(api.POST('/workspaces/{workspaceId}/smart-boards', { params: { path: { workspaceId: wsId } }, body })) as Promise<Board>,
    onSuccess: (b) => {
      qc.setQueryData<Board>(keys.board(b.id), b);
      void qc.invalidateQueries({ queryKey: keys.libraryBoards(b.library_id) });
      if (b.parent_board_id) {
        void qc.invalidateQueries({ queryKey: keys.boardChildren(b.parent_board_id) });
        void qc.invalidateQueries({ queryKey: keys.board(b.parent_board_id) });
      }
    },
  });
}

/** S3 `PUT /boards/{id}/smart`: a new definition refetches the board's listing and facets. `sort` omitted keeps it, null clears it. */
export function useUpdateSmartBoardDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ boardId, definition }: { boardId: string; definition: SmartBoardDefinition }) => req(api.PUT('/boards/{boardId}/smart', { params: { path: { boardId } }, body: definition })) as Promise<Board>,
    onSuccess: (b) => {
      qc.setQueryData<Board>(keys.board(b.id), (old) => (old ? { ...old, ...b, effective_mask: old.effective_mask, breadcrumbs: old.breadcrumbs } : b));
      void qc.invalidateQueries({ queryKey: keys.boardAssetsAll(b.id) });
      void qc.invalidateQueries({ queryKey: ['board-facets', b.id] });
      void qc.invalidateQueries({ queryKey: keys.libraryBoards(b.library_id) });
    },
  });
}

// --- release2/B ---
import type { Schemas as SchemasR2B } from './types';
type BoardFieldPolicies = SchemasR2B['BoardFieldPolicies'];
type FieldPolicy = SchemasR2B['FieldPolicy'];
type TagGroup = SchemasR2B['TagGroup'];
type TagCreationMode = SchemasR2B['TagCreationMode'];

/** G3: a board's own field policies plus the effective set over every asset field (`effective.source_board_id`). */
export function useFieldPolicies(boardId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.fieldPolicies(boardId ?? ''),
    queryFn: () => req(api.GET('/boards/{boardId}/field-policies', { params: { path: { boardId: boardId! } } })) as Promise<BoardFieldPolicies>,
    enabled: !!boardId && enabled,
    staleTime: 60_000,
  });
}

/** Replaces the board's set; descendants inherit, so every cached policy set and the board's cards refetch. */
export function useSetFieldPolicies(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: FieldPolicy[]) => req(api.PUT('/boards/{boardId}/field-policies', { params: { path: { boardId } }, body: { items } })) as Promise<BoardFieldPolicies>,
    onSuccess: (r) => {
      qc.setQueryData(keys.fieldPolicies(boardId), r);
      void qc.invalidateQueries({ queryKey: ['field-policies'] });
      void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
    },
  });
}

/** G1 tag groups, in display order (`sort_order`, then name). */
export function useTagGroups(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.tagGroups(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/tag-groups', { params: { path: { workspaceId: wsId! } } }))).items as TagGroup[],
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });
}

function useTagGroupsInvalidate(wsId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.tagGroups(wsId) });
    void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
  };
}

export function useCreateTagGroup(wsId: string) {
  const done = useTagGroupsInvalidate(wsId);
  return useMutation({
    mutationFn: (body: { name: string; color?: string; exclusive?: boolean }) => req(api.POST('/workspaces/{workspaceId}/tag-groups', { params: { path: { workspaceId: wsId } }, body })) as Promise<TagGroup>,
    onSuccess: done,
  });
}

export function useUpdateTagGroup(wsId: string) {
  const done = useTagGroupsInvalidate(wsId);
  return useMutation({
    mutationFn: ({ groupId, patch }: { groupId: string; patch: { name?: string; color?: string | null; exclusive?: boolean; sort_order?: number } }) =>
      req(api.PATCH('/workspaces/{workspaceId}/tag-groups/{tagGroupId}', { params: { path: { workspaceId: wsId, tagGroupId: groupId } }, body: patch })) as Promise<TagGroup>,
    onSuccess: done,
  });
}

export function useDeleteTagGroup(wsId: string) {
  const done = useTagGroupsInvalidate(wsId);
  return useMutation({
    mutationFn: (groupId: string) => req(api.DELETE('/workspaces/{workspaceId}/tag-groups/{tagGroupId}', { params: { path: { workspaceId: wsId, tagGroupId: groupId } } })),
    onSuccess: done,
  });
}

/** Moves tags into a group (`assignTagGroupTags`); `groupId: null` ungroups them one by one through `updateTag`. */
export function useAssignTagGroup(wsId: string) {
  const done = useTagGroupsInvalidate(wsId);
  return useMutation({
    mutationFn: async ({ groupId, tagIds }: { groupId: string | null; tagIds: string[] }) => {
      if (groupId) return req(api.POST('/workspaces/{workspaceId}/tag-groups/{tagGroupId}/tags', { params: { path: { workspaceId: wsId, tagGroupId: groupId } }, body: { tag_ids: tagIds } }));
      for (const tagId of tagIds) await req(api.PATCH('/tags/{tagId}', { params: { path: { tagId } }, body: { group_id: null } }));
      return null;
    },
    onSuccess: done,
  });
}

export function useTagPolicy(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.tagPolicy(wsId ?? ''),
    queryFn: () => req(api.GET('/workspaces/{workspaceId}/tag-policy', { params: { path: { workspaceId: wsId! } } })) as Promise<{ tag_creation: TagCreationMode }>,
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });
}

export function useSetTagPolicy(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tag_creation: TagCreationMode) => req(api.PUT('/workspaces/{workspaceId}/tag-policy', { params: { path: { workspaceId: wsId } }, body: { tag_creation } })) as Promise<{ tag_creation: TagCreationMode }>,
    onSuccess: (r) => qc.setQueryData(keys.tagPolicy(wsId), r),
  });
}

// --- release2/C ---
import type { Schemas as SchemasR2C } from './types';
import { rightsStatusOf } from '@/features/rights/status';
export type AssetRights = SchemasR2C['AssetRights'];
export type RightsExpiryAction = SchemasR2C['RightsExpiryAction'];
export type RetentionPolicy = SchemasR2C['RetentionPolicy'];
export type BoardRetention = SchemasR2C['BoardRetention'];
export type RetentionPreview = SchemasR2C['RetentionPreview'];
export type BulkRightsResult = { succeeded: string[]; failed: Array<{ asset_id: string; code: string; message?: string }>; job_id?: string | null };

/** C4: the card carries only the expiry; patch it (and the derived status) wherever the asset is cached. */
function patchRightsExpiry(qc: ReturnType<typeof useQueryClient>, ids: readonly string[], expiresAt: string | null): void {
  const status = rightsStatusOf(expiresAt);
  for (const id of ids) {
    patchCardEverywhere(qc, { id, rights_expires_at: expiresAt, rights_status: status } as AssetCard);
    qc.setQueryData<Asset>(keys.asset(id), (old) => (old ? { ...old, rights: old.rights ? { ...old.rights, expires_at: expiresAt, status } : old.rights } : old));
  }
}

/** C4 — replace an asset's rights record (keys left out are cleared). */
export function useSetAssetRights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, rights }: { assetId: string; rights: AssetRights }) => req(api.PUT('/assets/{assetId}/rights', { params: { path: { assetId } }, body: rights })) as Promise<AssetRights>,
    onSuccess: (r, v) => {
      qc.setQueryData<Asset>(keys.asset(v.assetId), (old) => (old ? { ...old, rights: r } : old));
      patchRightsExpiry(qc, [v.assetId], r.expires_at ?? null);
    },
  });
}

/** C4 — bulk rights: `merge` (default) changes only the keys given; EDIT_METADATA is checked per asset (`failed`). */
export function useBulkSetAssetRights(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { asset_ids: string[]; rights: AssetRights; merge?: boolean }) => req(api.POST('/workspaces/{workspaceId}/assets/rights', { params: { path: { workspaceId: wsId } }, body })) as Promise<BulkRightsResult>,
    onSuccess: (r, v) => {
      if (v.rights.expires_at !== undefined || v.merge === false) patchRightsExpiry(qc, r.succeeded, v.rights.expires_at ?? null);
      // the rest of the record is not on cards; refetch the detail queries (the WS `asset.updated` does the same)
      if (r.succeeded.length <= 50) for (const id of r.succeeded) void qc.invalidateQueries({ queryKey: keys.asset(id) });
      else void qc.invalidateQueries({ queryKey: ['asset'] });
    },
  });
}

export function useRightsPolicy(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.rightsPolicy(wsId ?? ''),
    queryFn: () => req(api.GET('/workspaces/{workspaceId}/rights-policy', { params: { path: { workspaceId: wsId! } } })) as Promise<{ on_expiry: RightsExpiryAction }>,
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });
}

export function useSetRightsPolicy(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on_expiry: RightsExpiryAction) => req(api.PUT('/workspaces/{workspaceId}/rights-policy', { params: { path: { workspaceId: wsId } }, body: { on_expiry } })) as Promise<{ on_expiry: RightsExpiryAction }>,
    onSuccess: (r) => qc.setQueryData(keys.rightsPolicy(wsId), r),
  });
}

/** S7b — the board's own retention policy, the effective one and where it comes from. */
export function useBoardRetention(boardId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.boardRetention(boardId ?? ''),
    queryFn: () => req(api.GET('/boards/{boardId}/retention', { params: { path: { boardId: boardId! } } })) as Promise<BoardRetention>,
    enabled: !!boardId && enabled,
  });
}

export function useSetBoardRetention(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policy: RetentionPolicy | null) => req(api.PUT('/boards/{boardId}/retention', { params: { path: { boardId } }, body: { policy } })) as Promise<BoardRetention>,
    onSuccess: (r) => {
      qc.setQueryData(keys.boardRetention(boardId), r);
      // descendants inherit; `Board.has_retention` changes
      void qc.invalidateQueries({ queryKey: ['board-retention'] });
      void qc.invalidateQueries({ queryKey: keys.board(boardId) });
    },
  });
}

/** S7b — what the nightly job would archive under `policy` (omitted = the saved one, null = as if cleared). */
export function usePreviewBoardRetention(boardId: string) {
  return useMutation({
    mutationFn: (policy?: RetentionPolicy | null) => req(api.POST('/boards/{boardId}/retention/preview', { params: { path: { boardId } }, body: policy === undefined ? {} : { policy } })) as Promise<RetentionPreview>,
  });
}

// --- release3/D ---
import type { Schemas } from './types';
/**
 * S2 — set a board's visibility (and, for ADMINs, its owner). The body is `BoardUpdate`'s Release 3 fields; typed loosely
 * so the hook compiles before the regenerated client types carry them. Lists are invalidated because a board that turned
 * private disappears from other members' trees.
 */
export function useSetBoardVisibility(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { visibility: 'workspace' | 'private'; owner_member_id?: string }) =>
      req(api.PATCH('/boards/{boardId}', { params: { path: { boardId } }, body: patch as never })) as Promise<Board>,
    onSuccess: (b) => {
      qc.setQueryData(keys.board(boardId), b);
      void qc.invalidateQueries({ queryKey: keys.libraryBoards(b.library_id) });
      if (b.parent_board_id) void qc.invalidateQueries({ queryKey: keys.boardChildren(b.parent_board_id) });
      void qc.invalidateQueries({ queryKey: keys.boardTree(b.workspace_id) });
    },
  });
}

/** DEL-2 — guests of a board (members with the guest role holding an override on it). */
export function useBoardGuests(boardId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.boardGuests(boardId ?? ''),
    queryFn: async () => (await req(api.GET('/boards/{boardId}/guests', { params: { path: { boardId: boardId! } } }))).items as Schemas['BoardGuest'][],
    enabled: !!boardId && enabled,
  });
}
function invalidateGuests(qc: ReturnType<typeof useQueryClient>, boardId: string) {
  void qc.invalidateQueries({ queryKey: keys.boardGuests(boardId) });
  void qc.invalidateQueries({ queryKey: ['members'] });
}
export function useInviteBoardGuest(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['BoardGuestInvite']) => req(api.POST('/boards/{boardId}/guests', { params: { path: { boardId } }, body })) as Promise<Schemas['BoardGuest']>,
    onSuccess: () => invalidateGuests(qc, boardId),
  });
}
export function useUpdateBoardGuest(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, access }: { memberId: string; access: Schemas['GuestAccess'] }) =>
      req(api.PATCH('/boards/{boardId}/guests/{memberId}', { params: { path: { boardId, memberId } }, body: { access } })) as Promise<Schemas['BoardGuest']>,
    onSuccess: () => invalidateGuests(qc, boardId),
  });
}
export function useRemoveBoardGuest(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => req(api.DELETE('/boards/{boardId}/guests/{memberId}', { params: { path: { boardId, memberId } } })),
    onSuccess: () => invalidateGuests(qc, boardId),
  });
}
/** `listMembers` with the DEL-2 `guests` filter (the API default is `exclude`; settings list everyone). */
export function useMembersD(wsId: string | undefined, guests: 'include' | 'exclude' | 'only' = 'include') {
  return useQuery({
    queryKey: [...keys.members(wsId ?? ''), { guests }],
    queryFn: () => fetchAllPages((cursor, limit) => req(api.GET('/workspaces/{workspaceId}/members', { params: { path: { workspaceId: wsId! }, query: { cursor, limit, guests } } }))),
    enabled: !!wsId,
  });
}

// --- release3/A --- notifications (REV-1), board follow + activity (REV-2)
import type { QueryClient } from '@tanstack/react-query';
import type { Schemas as SchemasR3A } from './types';
import type { BoardActivityScopeParam, NotificationsParams } from './keys';
export type Notification = SchemasR3A['Notification'];
export type NotificationPage = SchemasR3A['NotificationPage'];
export type NotificationPreferences = SchemasR3A['NotificationPreferences'];
export type NotificationKind = SchemasR3A['NotificationKind'];
export type NotificationChannel = SchemasR3A['NotificationChannel'];
export type BoardActivityItem = SchemasR3A['BoardActivityItem'];
export type BoardActivityPage = SchemasR3A['BoardActivityPage'];

export const NOTIFICATIONS_PAGE_SIZE = 50;

/** The caller's inbox (newest first) with the unread total on every page. `kinds` / `unread` narrow the list. */
export function useNotifications(wsId: string | undefined, params: NotificationsParams = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.notifications(wsId ?? '', params),
    queryFn: async ({ pageParam }) =>
      (await req(
        api.GET('/workspaces/{workspaceId}/notifications', {
          params: { path: { workspaceId: wsId! }, query: { cursor: pageParam, limit: NOTIFICATIONS_PAGE_SIZE, ...(params.kinds?.length ? { kinds: params.kinds.join(',') } : {}), ...(params.unread ? { unread: true } : {}) } },
        }),
      )) as NotificationPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!wsId && enabled,
    staleTime: 30_000,
  });
}

/** `POST /notifications/read` with `{ids}` or `{all: true}`; the cache is patched at once (the `notification.read` event confirms it). */
export function useMarkNotificationsRead(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[] } | { all: true }) => req(api.POST('/workspaces/{workspaceId}/notifications/read', { params: { path: { workspaceId: wsId } }, body })) as Promise<{ updated: number }>,
    onMutate: (body) => markReadInCache(qc, wsId, 'all' in body ? null : body.ids),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.notificationsAll(wsId) }),
  });
}

/** Patch every cached inbox page: `ids` null = everything read. Returns how many rows flipped. */
export function markReadInCache(qc: QueryClient, wsId: string, ids: string[] | null): number {
  const at = new Date().toISOString();
  let flipped = 0;
  qc.setQueriesData<{ pages: NotificationPage[]; pageParams: unknown[] }>({ queryKey: keys.notificationsAll(wsId) }, (old) => {
    if (!old) return old;
    let n = 0;
    const pages = old.pages.map((pg) => {
      const items = pg.items.map((it) => {
        if (it.read_at || (ids && !ids.includes(it.id))) return it;
        n++;
        return { ...it, read_at: at };
      });
      return { ...pg, items, unread_count: Math.max(0, pg.unread_count - n) };
    });
    flipped = Math.max(flipped, n);
    return { ...old, pages };
  });
  return flipped;
}

export function useNotificationPreferences(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.notificationPrefs(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/notification-preferences', { params: { path: { workspaceId: wsId! } } }))) as NotificationPreferences,
    enabled: !!wsId,
    staleTime: 60_000,
  });
}

export function useSetNotificationPreferences(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationPreferences) => req(api.PUT('/workspaces/{workspaceId}/notification-preferences', { params: { path: { workspaceId: wsId } }, body })) as Promise<NotificationPreferences>,
    onSuccess: (p) => qc.setQueryData(keys.notificationPrefs(wsId), p),
  });
}

/** Follow / unfollow a board; `Board.followed` flips at once in the cached board. */
export function useFollowBoard(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (follow: boolean) => {
      if (follow) await req(api.PUT('/boards/{boardId}/follow', { params: { path: { boardId } } }));
      else await req(api.DELETE('/boards/{boardId}/follow', { params: { path: { boardId } } }));
      return follow;
    },
    onMutate: (follow) => {
      const prev = qc.getQueryData<Board>(keys.board(boardId));
      qc.setQueryData<Board>(keys.board(boardId), (old) => (old ? { ...old, followed: follow } : old));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.board(boardId), ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.board(boardId) }),
  });
}

/** Recent comments + audit activity of the board's subtree (what the caller can VIEW), newest first. */
export function useBoardActivity(boardId: string | undefined, kinds: BoardActivityScopeParam = 'all', enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.boardActivity(boardId ?? '', kinds),
    queryFn: async ({ pageParam }) =>
      (await req(api.GET('/boards/{boardId}/activity', { params: { path: { boardId: boardId! }, query: { cursor: pageParam, limit: 50, kinds } } }))) as BoardActivityPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!boardId && enabled,
    staleTime: 30_000,
  });
}

// --- release3/B --- share-link delivery policy (DEL-1), find similar (SRCH-1), board defaults (META-2)
import type { SimilarParams } from './keys';
import type { Schemas as SchemasR3B } from './types';
type SimilarAssetsPage = SchemasR3B['SimilarAssetsPage'];
type BoardDefaults = SchemasR3B['BoardDefaults'];
type BoardDefaultsView = SchemasR3B['BoardDefaultsView'];

/** SRCH-1: visually similar assets of the active version (best first, not paginated); 404 `embedding_missing` surfaces as the error. */
export function useSimilarAssets(assetId: string | undefined, params: SimilarParams = {}, enabled = true) {
  return useQuery({
    queryKey: keys.similar(assetId ?? '', params),
    queryFn: () => req(api.GET('/assets/{assetId}/similar', { params: { path: { assetId: assetId! }, query: params } })) as Promise<SimilarAssetsPage>,
    enabled: !!assetId && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** META-2: a board's own defaults plus the effective (inherited) set. */
export function useBoardDefaults(boardId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.boardDefaults(boardId ?? ''),
    queryFn: () => req(api.GET('/boards/{boardId}/defaults', { params: { path: { boardId: boardId! } } })) as Promise<BoardDefaultsView>,
    enabled: !!boardId && enabled,
    staleTime: 60_000,
  });
}

/** Replaces the board's own defaults; descendants inherit, so every cached defaults view refetches. */
export function useSetBoardDefaults(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BoardDefaults) => req(api.PUT('/boards/{boardId}/defaults', { params: { path: { boardId } }, body })) as Promise<BoardDefaultsView>,
    onSuccess: (r) => {
      qc.setQueryData(keys.boardDefaults(boardId), r);
      void qc.invalidateQueries({ queryKey: ['board-defaults'] });
    },
  });
}

/** DEL-1: give a name and e-mail on a link with `require_identity`; the resolve is refetched so the content loads. */
export function useShareIdentify(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string }) => req(api.POST('/public/share/{token}/identify', { params: { path: { token } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.share(token) }),
  });
}

/** DEL-1: `Asset.share_excluded` ("internal only") through `updateAsset`; the detail cache is patched optimistically. */
export function useSetShareExcluded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, value }: { assetId: string; value: boolean }) => req(api.PATCH('/assets/{assetId}', { params: { path: { assetId } }, body: { share_excluded: value } })) as Promise<Asset>,
    onMutate: async ({ assetId, value }) => {
      await qc.cancelQueries({ queryKey: keys.asset(assetId) });
      const prev = qc.getQueryData<Asset>(keys.asset(assetId));
      if (prev) qc.setQueryData<Asset>(keys.asset(assetId), { ...prev, share_excluded: value });
      return { prev };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.asset(v.assetId), ctx.prev);
    },
    onSuccess: (asset) => patchCardEverywhere(qc, asset),
  });
}

/** DEL-1: bulk `set_share_excluded {value}` (EDIT_METADATA per asset; failures come back per asset). */
export function useBulkSetShareExcluded(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, value }: { ids: string[]; value: boolean }) =>
      req(api.POST('/workspaces/{workspaceId}/assets/bulk', { params: { path: { workspaceId: wsId } }, body: { asset_ids: ids, action: 'set_share_excluded', params: { value } } as never })) as Promise<{ succeeded: string[]; failed: { asset_id: string; code: string; message: string }[] }>,
    onSuccess: (_r, v) => {
      if (v.ids.length <= 50) for (const id of v.ids) void qc.invalidateQueries({ queryKey: keys.asset(id) });
      else void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['board-assets'] });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

// --- release3/C ---
import type { Schemas as SchemasR3C } from './types';
export type DownloadPreset = SchemasR3C['DownloadPreset'];
export type ImageTransform = SchemasR3C['ImageTransform'];
export type DownloadPresetCreate = SchemasR3C['DownloadPresetCreate'];
export type DownloadPresetUpdate = SchemasR3C['DownloadPresetUpdate'];
export type DownloadResponse = SchemasR3C['DownloadResponse'];
export type Job = SchemasR3C['Job'];
/** `downloadVersion` with a transform: 200 `DownloadResponse` (cached export) or 202 `Job` (`export_render`, poll `getJob`). */
export type ExportVersionResult = { kind: 'url'; download: DownloadResponse } | { kind: 'job'; job: Job };

/** EXP-1 — the workspace export presets (VIEW; guests included), in display order. */
export function useDownloadPresets(wsId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.downloadPresets(wsId ?? ''),
    queryFn: async () => (await req(api.GET('/workspaces/{workspaceId}/download-presets', { params: { path: { workspaceId: wsId! } } }))).items as DownloadPreset[],
    enabled: !!wsId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateDownloadPreset(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DownloadPresetCreate) => req(api.POST('/workspaces/{workspaceId}/download-presets', { params: { path: { workspaceId: wsId } }, body })) as Promise<DownloadPreset>,
    onSuccess: (p) => {
      qc.setQueryData<DownloadPreset[]>(keys.downloadPresets(wsId), (old) => sortPresets([...(old ?? []).filter((x) => x.id !== p.id), p]));
      void qc.invalidateQueries({ queryKey: keys.downloadPresets(wsId) });
    },
  });
}

export function useUpdateDownloadPreset(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ presetId, patch }: { presetId: string; patch: DownloadPresetUpdate }) =>
      req(api.PATCH('/workspaces/{workspaceId}/download-presets/{presetId}', { params: { path: { workspaceId: wsId, presetId } }, body: patch })) as Promise<DownloadPreset>,
    onSuccess: (p) => {
      qc.setQueryData<DownloadPreset[]>(keys.downloadPresets(wsId), (old) => sortPresets((old ?? []).map((x) => (x.id === p.id ? p : x))));
      void qc.invalidateQueries({ queryKey: keys.downloadPresets(wsId) });
    },
  });
}

export function useDeleteDownloadPreset(wsId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presetId: string) => req(api.DELETE('/workspaces/{workspaceId}/download-presets/{presetId}', { params: { path: { workspaceId: wsId, presetId } } })),
    onSuccess: (_r, presetId) => {
      qc.setQueryData<DownloadPreset[]>(keys.downloadPresets(wsId), (old) => (old ?? []).filter((x) => x.id !== presetId));
      void qc.invalidateQueries({ queryKey: keys.downloadPresets(wsId) });
    },
  });
}

function sortPresets(list: DownloadPreset[]): DownloadPreset[] {
  return list.slice().sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/** The inline query fields of `downloadVersion` for a transform (`preset_id` wins when given). */
export function exportQuery(input: { presetId?: string | null; transform?: ImageTransform | null }): Record<string, string | number | boolean> {
  if (input.presetId) return { preset_id: input.presetId };
  const t = input.transform;
  if (!t) return {};
  const q: Record<string, string | number | boolean> = { format: t.format };
  if (t.fit) q.fit = t.fit;
  if (t.fit === 'scale' && t.scale !== undefined) q.scale = t.scale;
  if (t.fit === 'long_edge' && t.long_edge !== undefined) q.long_edge = t.long_edge;
  if (t.fit === 'box') {
    if (t.width !== undefined) q.width = t.width;
    if (t.height !== undefined) q.height = t.height;
  }
  if (t.quality !== undefined && t.format !== 'png' && t.format !== 'original') q.quality = t.quality;
  if (t.strip_metadata === false) q.strip_metadata = false;
  if (t.suffix) q.suffix = t.suffix;
  return q;
}

/**
 * EXP-1 — request an export of a version: a cached rendition answers 200 with the signed url, otherwise 202 with the
 * `export_render` job to poll (`GET /jobs/{jobId}`); the caller follows `result.url` when it is done.
 */
export function useExportVersion() {
  return useMutation({
    mutationFn: async ({ versionId, presetId, transform }: { versionId: string; presetId?: string | null; transform?: ImageTransform | null }): Promise<ExportVersionResult> => {
      const r = await api.GET('/versions/{versionId}/download', { params: { path: { versionId }, query: exportQuery({ presetId, transform }) as never } });
      if (!r.response.ok) throw await ApiErrorR3C.fromResponse(r.response, r.error);
      const data = r.data as DownloadResponse | Job;
      if (r.response.status === 202 || (data && 'state' in data && 'kind' in data)) return { kind: 'job', job: data as Job };
      return { kind: 'url', download: data as DownloadResponse };
    },
  });
}
import { ApiError as ApiErrorR3C } from './errors';
